import { useMemo } from "react";
import { useBoardThicknesses } from "@/api/hooks/useBoardThicknesses";
import { useSplitRules } from "@/api/hooks/useSplitRules";
import type { SplitRule } from "@/api/schemas";
import {
  hrubkaLepeneho,
  kombinaciaZMaterialu,
  vrstvyZMaterialu,
} from "@/lib/splitRules";

export interface DostupnaHrubka {
  mm: number;
  /** true = hrúbka existuje len ako lepená (split rule), nie ako doska. */
  lepena: boolean;
  /** Vrstvy lepenej hrúbky pri danom materiáli (18,7 + 18,7), ak sú známe. */
  vrstvy?: number[];
}

/**
 * Hrúbky, ktoré dáva zmysel ponúknuť konkrétnemu dielcu (KOLO 162).
 *
 * S plošným materiálom: hrúbka materiálu + lepené hrúbky, ktoré sa z neho
 * dajú zlepiť (materiál je jednou vrstvou). Bez materiálu: všetky hrúbky
 * z Nastavení + lepené. Vrstva sendviča lepená nebýva — len dosky.
 *
 * Lepená hrúbka je **skutočná** (KOLO 188): z dosky 18,7 je 36 = 37,4
 * a 28 = 28,7 (10 mm vrstva v triede) — také číslo dielec po zlepení má.
 */
export function hrubkyPreDielec(
  vsetky: DostupnaHrubka[],
  pravidla: SplitRule[],
  matZ: number | null,
  { lepene = true }: { lepene?: boolean } = {},
): DostupnaHrubka[] {
  if (matZ == null) {
    return lepene ? vsetky : vsetky.filter((h) => !h.lepena);
  }
  const out: DostupnaHrubka[] = [{ mm: matZ, lepena: false }];
  if (lepene) {
    for (const r of pravidla) {
      const combo = kombinaciaZMaterialu(r, matZ);
      if (!combo) continue;
      const vrstvy = vrstvyZMaterialu(combo, matZ);
      const mm = hrubkaLepeneho(vrstvy, r.hrubka_mm) ?? r.hrubka_mm;
      if (out.some((h) => h.mm === mm)) continue;
      out.push({ mm, lepena: true, vrstvy });
    }
  }
  return out.sort((a, b) => a.mm - b.mm);
}

/** Hrúbka plošného materiálu, ktorá viaže dielec — sklo a iné druhy nie. */
export function hrubkaMaterialu(
  m: { z_mm?: string | number | null; druh?: string | null; kategoria?: string | null } | null,
): number | null {
  if (!m || m.z_mm == null || m.druh !== "plosny") return null;
  if ((m.kategoria ?? "").startsWith("SKLO")) return null;
  const z = parseFloat(String(m.z_mm));
  return Number.isFinite(z) ? z : null;
}

/**
 * Hrúbky dosiek pre Korpus Builder, Dvere a Zásuvku (KOLO 168).
 *
 * Dovtedy tam bolo natvrdo 16/18/25, takže korpus zo škárovky 27 mm sa
 * vložiť nedal — server dielec, ktorého hrúbka nesedí s materiálom,
 * odmietne (422, KOLO 159/162). S plošným materiálom slotu je na výber
 * len jeho hrúbka, bez neho dosky z Nastavení. Lepené hrúbky nie —
 * korpus sa z dosiek skladá, nelepí.
 *
 * `aktualna` sa doplní, keď v zozname nie je: uložená konfigurácia alebo
 * komponent z knižnice s inou hrúbkou sa musí dať zobraziť, aby bolo
 * vidieť, čo nesedí (`nesuladHrubkySlotu`).
 */
export function hrubkyDosiekSlotu(
  vsetky: DostupnaHrubka[],
  matZ: number | null,
  aktualna: number | null = null,
): number[] {
  const mm = hrubkyPreDielec(vsetky, [], matZ, { lepene: false }).map((h) => h.mm);
  if (aktualna != null && Number.isFinite(aktualna) && !mm.includes(aktualna)) {
    mm.push(aktualna);
  }
  return mm.sort((a, b) => a - b);
}

/** Hrúbka po otvorení okna: materiál slotu, bez neho 18 mm (ak ju Nastavenia majú). */
export function predvolenaHrubkaDosky(
  matZ: number | null,
  vsetky: DostupnaHrubka[],
): number {
  if (matZ != null) return matZ;
  const dosky = vsetky.filter((h) => !h.lepena).map((h) => h.mm);
  return dosky.length === 0 || dosky.includes(18) ? 18 : dosky[0]!;
}

/** Hláška, keď hrúbka dosiek nesedí s plošným materiálom slotu — server by dielce odmietol. */
export function nesuladHrubkySlotu(t: number, matZ: number | null): string | null {
  if (matZ == null || t === matZ) return null;
  return (
    `Hrúbka ${t} mm nesedí s materiálom (${matZ} mm) — dielce ` +
    `by sa neuložili. Vyber ${matZ} mm alebo iný materiál.`
  );
}

/**
 * Hrúbky dostupné pre dielec = hrúbky dosiek z Nastavení ∪ lepené hrúbky
 * zo split rules. Zoradené vzostupne. Zdroj pre Z select/datalist.
 */
export function useDostupneHrubky(): DostupnaHrubka[] {
  const boardQuery = useBoardThicknesses();
  const splitQuery = useSplitRules();
  const boards = boardQuery.data?.data;
  const splits = splitQuery.data?.data;

  return useMemo(() => {
    const boardMm = new Set((boards ?? []).map((b) => b.hrubka_mm));
    const splitMm = new Set((splits ?? []).map((r) => r.hrubka_mm));
    const all = [...new Set([...boardMm, ...splitMm])].sort((a, b) => a - b);
    return all.map((mm) => ({ mm, lepena: !boardMm.has(mm) }));
  }, [boards, splits]);
}
